import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Store as StoreIcon,
  ShoppingCart as OrderIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  LocalShipping as DeliveryIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { getOrderPayableTotal } from '../../utils/orderAmount';
import api from '../../utils/api';
import OrderModal from '../../components/common/OrderModal';

const ViewUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { actions } = useApp();
  
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUser();
      fetchUserOrders();
    }
  }, [userId]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users/${userId}`);
      if (response.data.success) {
        setUser(response.data.user);
      } else {
        setError('User not found');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setError(error.response?.data?.message || 'Error fetching user details');
      actions.showSnackbar('Error fetching user details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserOrders = async () => {
    try {
      setOrdersLoading(true);
      // This would be an API call to get user orders
      // For now, we'll simulate this since the orders API might not exist yet
      const response = await api.get(`/orders/user/${userId}`);
      if (response.data.success) {
        setOrders(response.data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching user orders:', error);
      // Don't show error for orders if the API doesn't exist yet
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle different timestamp formats
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp?.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getOrderStatus = (order) => {
    return order.order_status || order.status || order.orderStatus || 'pending';
  };

  const getOrderAmount = (order) => getOrderPayableTotal(order);

  const getOrderDate = (order) => {
    return order.timeStamp || order.createdAt || order.orderDate || order.timestamp || order.created_at;
  };

  const getOrderId = (order) => {
    return order.orderId || order.id || 'N/A';
  };

  const getDeliveryDate = (order) => {
    return order.delivery_date || 'N/A';
  };

  const getPaymentMethod = (order) => {
    const method = order.payment_method || 'N/A';
    return method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getOrderStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'delivered':
      case 'completed':
        return 'success';
      case 'out_for_delivery':
      case 'processing':
      case 'confirmed':
        return 'warning';
      case 'cancelled':
      case 'failed':
        return 'error';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleViewOrderDetails = async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      if (response.data.success) {
        setSelectedOrder(response.data.order);
        setOrderModalOpen(true);
      } else {
        actions.showSnackbar('Order not found', 'error');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      actions.showSnackbar('Error fetching order details', 'error');
    }
  };

  const handleCloseOrderModal = () => {
    setOrderModalOpen(false);
    setSelectedOrder(null);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'admin': return 'warning';
      case 'store_admin': return 'info';
      case 'user': return 'success';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'store_admin': return 'Store Admin';
      case 'admin': return 'Admin';
      case 'user': return 'User';
      default: return role;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/super-admin/users')}
        >
          Back to Users
        </Button>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          User not found
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/super-admin/users')}
        >
          Back to Users
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/super-admin/users')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          User Details
        </Typography>
      </Box>

      {/* User Information */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1 }} />
            User Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={3} sx={{ justifyContent: 'space-evenly' }}>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {user.f_name || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Mobile Number
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {user.mobileNo || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {user.linkedStore && (
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StoreIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Linked Store
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {user.linkedStore}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created On
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatDate(user.timeStamp)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Order History */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <OrderIcon sx={{ mr: 1 }} />
            Order History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {ordersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={30} />
            </Box>
          ) : orders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <OrderIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No orders found for this user
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Order ID</strong></TableCell>
                    <TableCell><strong>Order Date</strong></TableCell>
                    <TableCell><strong>Delivery Date</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Payment</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders
                    .sort((a, b) => {
                      const dateA = getOrderDate(a) || 0;
                      const dateB = getOrderDate(b) || 0;
                      return dateB - dateA; // Sort in descending order (latest first)
                    })
                    .map((order, index) => (
                    <TableRow key={order.id || order.orderId || index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          #{getOrderId(order)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(getOrderDate(order))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getDeliveryDate(order) !== 'N/A' 
                            ? new Date(getDeliveryDate(order)).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : 'N/A'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(getOrderAmount(order))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {getPaymentMethod(order)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getOrderStatus(order).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                          color={getOrderStatus(order).toLowerCase() === 'assigned' ? undefined : getOrderStatusColor(getOrderStatus(order))}
                          size="small"
                          sx={
                            getOrderStatus(order).toLowerCase() === 'assigned'
                              ? { bgcolor: '#2e7d32', color: '#fff', '& .MuiChip-label': { color: '#fff' } }
                              : undefined
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewOrderDetails(getOrderId(order))}
                          sx={{ minWidth: 'auto' }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      <OrderModal 
        open={orderModalOpen}
        onClose={handleCloseOrderModal}
        order={selectedOrder}
      />
    </Box>
  );
};

export default ViewUser; 